import { useContext, useState } from 'react';
import { UserCircleIcon, KeyIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import axios from '../../../utils/axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';

/* ── Section card wrapper ──────────────────────────────────────────── */
function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
      <p className="text-white/70 text-sm font-semibold mb-0.5">{title}</p>
      {subtitle && <p className="text-white/30 text-xs mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

/* ── Labelled text input ───────────────────────────────────────────── */
function Field({
  label, value, onChange, type = 'text', placeholder = '',
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
      />
    </div>
  );
}

/* ── Main view ─────────────────────────────────────────────────────── */
export default function ProfileSettingsView() {
  const { state, dispatch } = useContext(AuthContext);
  const user     = state.userData;
  const token    = state.userToken ?? '';

  /* Profile form */
  const [name,    setName]    = useState(user?.name    ?? '');
  const [country, setCountry] = useState(user?.country ?? '');
  const [saving,  setSaving]  = useState(false);
  const [profMsg, setProfMsg] = useState<{ ok: boolean; text: string } | null>(null);

  /* Password form */
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving,  setPwSaving]  = useState(false);
  const [pwMsg,     setPwMsg]     = useState<{ ok: boolean; text: string } | null>(null);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setProfMsg({ ok: false, text: 'Name cannot be empty.' }); return; }
    setSaving(true); setProfMsg(null);
    try {
      const res = await axios.put(
        `${REACT_APP_UPLOADS}/users/me`,
        { name: name.trim(), country: country.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.data;
      // Push updated user data into auth context
      if (updated) {
        dispatch({
          type: 'SIGN_IN',
          token,
          data: { ...user!, name: updated.name, country: updated.country },
        });
      }
      setProfMsg({ ok: true, text: 'Profile updated successfully.' });
    } catch (err: any) {
      setProfMsg({ ok: false, text: err?.response?.data?.message ?? 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPw.length < 8) { setPwMsg({ ok: false, text: 'New password must be at least 8 characters.' }); return; }
    if (newPw !== confirmPw) { setPwMsg({ ok: false, text: 'Passwords do not match.' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await axios.put(
        `${REACT_APP_UPLOADS}/users/me`,
        { currentPassword: currentPw, newPassword: newPw },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPwMsg({ ok: true, text: 'Password changed successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setPwMsg({ ok: false, text: err?.response?.data?.message ?? 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Profile &amp; Settings</h2>
        <p className="text-white/30 text-xs mt-0.5">Manage your account information and security</p>
      </div>

      <div className="max-w-2xl flex flex-col gap-5">

        {/* ── Account info (read-only) ─────────────────────────────── */}
        <Card title="Account" subtitle="Your login identity — cannot be changed">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-900/50 border border-green-500/20 flex items-center justify-center shrink-0">
              <UserCircleIcon className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-white/80 text-sm font-semibold">{user?.name ?? '—'}</p>
              <p className="text-white/40 text-xs mt-0.5">{user?.username ?? '—'}</p>
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[9px] font-semibold uppercase tracking-wider">
                {user?.role ?? 'User'}
              </span>
            </div>
          </div>
        </Card>

        {/* ── Edit profile ─────────────────────────────────────────── */}
        <Card title="Edit Profile" subtitle="Update your display name and country">
          <form onSubmit={saveProfile} className="flex flex-col gap-3">
            <Field label="Full Name" value={name} onChange={setName} placeholder="Your full name" />
            <Field label="Country" value={country} onChange={setCountry} placeholder="e.g. Kenya" />

            {profMsg && (
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
                profMsg.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {profMsg.ok && <CheckCircleIcon className="w-4 h-4 shrink-0" />}
                {profMsg.text}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Card>

        {/* ── Change password ───────────────────────────────────────── */}
        <Card title="Change Password" subtitle="Requires your current password to confirm">
          <form onSubmit={changePassword} className="flex flex-col gap-3">
            <Field label="Current Password" value={currentPw} onChange={setCurrentPw} type="password" placeholder="••••••••" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="New Password" value={newPw} onChange={setNewPw} type="password" placeholder="Min. 8 characters" />
              <Field label="Confirm New Password" value={confirmPw} onChange={setConfirmPw} type="password" placeholder="Repeat new password" />
            </div>

            {/* Password strength bar */}
            {newPw.length > 0 && (
              <div>
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3].map((i) => {
                    const strength = newPw.length < 8 ? 0 : newPw.length < 12 || !/[A-Z]/.test(newPw) ? 1 : newPw.length < 16 || !/\d/.test(newPw) ? 2 : 3;
                    return (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full transition-all duration-300"
                        style={{
                          background: i <= strength
                            ? strength === 1 ? '#f59e0b' : strength === 2 ? '#3b82f6' : '#22c55e'
                            : 'rgba(255,255,255,0.08)',
                        }}
                      />
                    );
                  })}
                </div>
                <p className="text-white/30 text-[10px]">
                  {newPw.length < 8 ? 'Too short' : newPw.length < 12 ? 'Weak' : newPw.length < 16 ? 'Fair' : 'Strong'}
                </p>
              </div>
            )}

            {pwMsg && (
              <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
                pwMsg.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {pwMsg.ok && <CheckCircleIcon className="w-4 h-4 shrink-0" />}
                {pwMsg.text}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={pwSaving}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
              >
                <KeyIcon className="w-3.5 h-3.5" />
                {pwSaving ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </Card>

        {/* ── Danger zone ──────────────────────────────────────────── */}
        <div className="rounded-xl border border-red-500/15 p-5" style={{ background: 'rgba(239,68,68,0.04)' }}>
          <p className="text-red-400/80 text-sm font-semibold mb-1">Danger Zone</p>
          <p className="text-white/30 text-xs mb-4">Permanently delete your account and all associated data. This cannot be undone.</p>
          <button
            className="px-4 py-2 rounded-lg border border-red-500/30 text-red-400 text-xs font-semibold hover:bg-red-500/10 transition-colors"
            onClick={() => window.open('mailto:support@wits.lapa.africa?subject=Account%20Deletion%20Request', '_blank')}
          >
            Request Account Deletion
          </button>
        </div>

      </div>
    </div>
  );
}
